import React, { PureComponent } from 'react';
import get from 'lodash/get';
import dayjs from 'dayjs';
import { Form, Radio, Checkbox, Button, Popconfirm, message, Input } from 'antd';
import Currency from '$app/components/vendor/Currency';
import Modal from '../../../../../components/Modal';
import Copyable from '$app/shared/components/Copyable';
import { poundsToPence } from '$app/utils/util';
import { deleteCustomerPromo, saveCustomerPromo } from '../api';
import BookingPageContext from '../../../context';

const FormItem = Form.Item;

class PromoEditModal extends PureComponent {
  static contextType = BookingPageContext;

  state = {
    confirmLoading: false,
    initialAmount: null,
  };

  componentDidMount() {
    this.setInitialAmount();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.customerServicePromo !== this.props.customerServicePromo) {
      this.setInitialAmount();
    }
  }

  setInitialAmount = () => {
    const { customerServicePromo } = this.props;
    const isPending = customerServicePromo?.status === 'PENDING';
    const amount = this.getAmount(customerServicePromo, isPending);
    this.setState({ initialAmount: amount });
  };

  onValidateForm = () => {
    const { closePopup, customer, customerServicePromo, segment } = this.props;

    this.props.form.validateFields(async (err, values) => {
      if (err) return;

      try {
        this.setState({ confirmLoading: true });
        const { response, isPending } = await saveCustomerPromo({
          customer,
          customerServicePromo,
          patch: {
            ...values,
            code: customerServicePromo?.code || '',
          },
          segment,
        });

        if (response.statusCode && response.statusCode !== 200) {
          this.setState({ confirmLoading: false });
          return;
        }

        message.success(isPending ? 'Promo added successfully' : 'Promo modified successfully');
        closePopup();
        this.context.reloadBooking();
      } catch (error) {
        message.error(get(error, 'response.data.message', error.message));
        this.setState({ confirmLoading: false });
      }
    });
  };

  onDelete = async () => {
    const { closePopup, customer, customerServicePromo } = this.props;
    const campaignId = customerServicePromo?.campaignId;

    if (!campaignId) return;

    try {
      this.setState({ confirmLoading: true });
      await deleteCustomerPromo(customer.id, campaignId);
      message.success('Promo code deleted successfully');
      closePopup();
      this.context.reloadBooking();
    } catch (error) {
      message.error(get(error, 'response.data.message', error.message));
      this.setState({ confirmLoading: false });
    }
  };

  getAmount = (customerServicePromo, isPending) => {
    if (isPending) return 5;
    if (customerServicePromo?.discountAmountPence) return customerServicePromo.discountAmountPence / 100;
    return 5;
  };

  getButtonTextAndState = () => {
    const { customerServicePromo, form } = this.props;
    const { initialAmount } = this.state;
    const isPending = customerServicePromo?.status === 'PENDING';

    if (isPending) return { text: 'Add', disabled: false };

    const currentAmount = form.getFieldValue('amount');
    const amountChanged = currentAmount !== initialAmount;

    if (amountChanged) {
      return { text: 'Change Discount Code Amount', disabled: false };
    }

    return { text: 'Add', disabled: true };
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    const { closePopup, customerServicePromo, openedPopup } = this.props;
    const isPending = customerServicePromo?.status === 'PENDING';
    const amount = this.getAmount(customerServicePromo, isPending);
    const promoCode = customerServicePromo?.code || '';
    const validUntilDate = customerServicePromo?.validUntilDate
      ? dayjs(customerServicePromo.validUntilDate).format('DD/MM/YYYY')
      : null;
    const buttonState = this.getButtonTextAndState();

    const footer = [
      !isPending && (
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this promo code?"
          onConfirm={this.onDelete}
          okText="Yes"
          cancelText="No"
        >
          <Button disabled={this.state.confirmLoading}>
            <img
              alt="delete"
              src="/assets/icons/bin-1.svg"
              width={16}
              style={{ position: 'relative', top: -1, marginRight: 6 }}
            />
            Delete code
          </Button>
        </Popconfirm>
      ),
      <Button key="cancel" onClick={closePopup} disabled={this.state.confirmLoading}>
        Cancel
      </Button>,
      <Button
        key="submit"
        type="primary"
        onClick={this.onValidateForm}
        disabled={buttonState.disabled}
        loading={this.state.confirmLoading}
      >
        {buttonState.text}
      </Button>,
    ].filter(Boolean);

    return (
      <Modal title="Issue Promo Code" visible={openedPopup === 'PromoEditModal'} onCancel={closePopup} footer={footer}>
        <Form>
          <FormItem label="Amount" style={{ marginBottom: 0 }}>
            {getFieldDecorator('amount', {
              initialValue: amount,
            })(
              <Radio.Group onChange={() => this.forceUpdate()}>
                <Radio value={5}>
                  <Currency>{poundsToPence(5)}</Currency>
                </Radio>
                <Radio value={10}>
                  <Currency>{poundsToPence(10)}</Currency>
                </Radio>
              </Radio.Group>
            )}
          </FormItem>
          <FormItem label="Code" style={{ marginBottom: 0 }}>
            <Input.Group compact style={{ display: 'flex', alignItems: 'center' }}>
              <Input value={promoCode} disabled size="small" />
              <Copyable value={promoCode} />
            </Input.Group>
            {validUntilDate && (
              <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                Expires on {validUntilDate}
              </div>
            )}
          </FormItem>
          <FormItem style={{ marginBottom: 0 }}>
            {getFieldDecorator('applyToCheckout', {
              valuePropName: 'checked',
              initialValue: customerServicePromo?.appliedToCheckout ?? true,
            })(<Checkbox disabled={!isPending}>Apply discount from Checkout</Checkbox>)}
          </FormItem>
          <FormItem style={{ marginBottom: 0 }}>
            {getFieldDecorator('applyToEWO', {
              valuePropName: 'checked',
              initialValue: customerServicePromo?.appliedToEWO ?? true,
            })(<Checkbox disabled={!isPending}>Apply discount from EWO</Checkbox>)}
          </FormItem>
          <FormItem style={{ marginBottom: 0 }}>
            {getFieldDecorator('emailPromoCodeToCustomer', {
              valuePropName: 'checked',
              initialValue: true,
            })(<Checkbox disabled={!isPending}>Email the promo code to customer</Checkbox>)}
          </FormItem>
        </Form>
      </Modal>
    );
  }
}

export default Form.create({
  mapPropsToFields(props) {
    const customerServicePromo = get(props, 'customerServicePromo', null);
    const fields = {};
    const isPending = customerServicePromo?.status === 'PENDING';

    if (isPending) {
      fields.amount = Form.createFormField({
        value: 5,
      });
    } else if (customerServicePromo && customerServicePromo.discountAmountPence) {
      fields.amount = Form.createFormField({
        value: customerServicePromo.discountAmountPence / 100,
      });
    }

    if (customerServicePromo && customerServicePromo.appliedToCheckout !== undefined) {
      fields.applyToCheckout = Form.createFormField({
        value: customerServicePromo.appliedToCheckout,
      });
    }

    if (customerServicePromo && customerServicePromo.appliedToEWO !== undefined) {
      fields.applyToEWO = Form.createFormField({
        value: customerServicePromo.appliedToEWO,
      });
    }

    return fields;
  },
})(PromoEditModal);
